import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  Maximize2,
  Minus,
  Pencil,
  Plus,
  Share2,
  X
} from "lucide-react";
import { PhotoPager, type PhotoPagerHandle } from "../picslot-animation/src/PhotoPager";
import "../picslot-animation/src/photoPager.css";
import { useGalleryData } from "./api";
import { formatBytes } from "./model";
import { AuthControls, ErrorView, LoadingView, ThemedShell } from "./ui";
import { navigate } from "./navigation";

export function PhotoPage({ slug, photoId }: { slug: string; photoId: string }) {
  const { gallery, photos, error, loading } = useGalleryData(slug);
  const visiblePhotos = photos.filter((photo) => !photo.isHidden);
  const [currentId, setCurrentId] = useState(photoId);
  const [zoom, setZoom] = useState(1);
  const [showDetails, setShowDetails] = useState(false);
  const [status, setStatus] = useState("");
  const viewerRef = useRef<HTMLElement>(null);
  const pager = useRef<PhotoPagerHandle>(null);
  const currentIndex = visiblePhotos.findIndex((photo) => photo.id === currentId);
  const photo = visiblePhotos[currentIndex];

  const showIndex = useCallback((nextIndex: number) => {
    const next = visiblePhotos[nextIndex];
    if (!next) return;
    setCurrentId(next.id);
    setZoom(1);
    setShowDetails(false);
    setStatus("");
    window.history.replaceState(null, "", `/g/${slug}/photo/${next.id}`);
  }, [slug, visiblePhotos]);

  // Arrows and keys page through the pager so they slide like a swipe does.
  const goTo = useCallback((offset: 1 | -1) => {
    if (visiblePhotos.length > 1) pager.current?.go(offset);
  }, [visiblePhotos.length]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goTo(-1);
      if (event.key === "ArrowRight") goTo(1);
      if (event.key === "Escape") navigate(`/g/${slug}`);
      if (event.key === "+" || event.key === "=") setZoom((value) => Math.min(3, value + 0.5));
      if (event.key === "-") setZoom((value) => Math.max(1, value - 0.5));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goTo, slug]);

  const pagerPhotos = useMemo(() => visiblePhotos.map((item, index) => ({
    src: item.mediaUrl,
    alt: `Photo ${index + 1} from ${gallery?.displayName ?? "this gallery"}`
  })), [gallery?.displayName, visiblePhotos]);

  async function sharePhoto() {
    try {
      if (navigator.share) {
        await navigator.share({ title: gallery?.displayName, url: window.location.href });
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      setStatus("Photo link copied");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setStatus("Could not share this photo");
    }
  }

  async function enterFullscreen() {
    try {
      await viewerRef.current?.requestFullscreen();
    } catch {
      setStatus("Fullscreen is not available in this browser");
    }
  }

  if (loading) return <LoadingView />;
  if (error || !gallery || !photo) return <ErrorView message={error || "Photo not found"} />;

  const date = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(photo.createdAt));

  return (
    <ThemedShell theme={gallery.theme} className="photo-viewer-shell">
      <header className="viewer-topbar">
        <a href={`/g/${slug}`} className="back-link"><ArrowLeft size={18} /><span>Gallery</span></a>
        <div className="viewer-counter">{currentIndex + 1} / {visiblePhotos.length}</div>
        <div className="viewer-actions">
          <button className="icon-button" type="button" onClick={() => setShowDetails((value) => !value)} aria-label="Photo details" aria-expanded={showDetails} title="Details"><Info size={18} /></button>
          {gallery.isOwner && <a className="icon-button" href={`/g/${slug}/photo/${photo.id}/edit`} aria-label="Edit photo" title="Edit photo"><Pencil size={18} /></a>}
          <button className="icon-button" type="button" onClick={() => void sharePhoto()} aria-label="Share photo" title="Share"><Share2 size={18} /></button>
          <a className="icon-button" href={photo.originalUrl} download={photo.filename} aria-label="Download original" title="Download original"><Download size={18} /></a>
          <AuthControls />
        </div>
      </header>

      <section className="viewer-stage" ref={viewerRef}>
        {visiblePhotos.length > 1 && <button className="viewer-nav previous" type="button" onClick={() => goTo(-1)} aria-label="Previous photo"><ChevronLeft size={26} /></button>}
        <PhotoPager ref={pager} className="photo-page-pager" photos={pagerPhotos} index={currentIndex} onIndexChange={showIndex} zoom={zoom} onZoomChange={setZoom} minZoom={1} maxZoom={3} />
        {visiblePhotos.length > 1 && <button className="viewer-nav next" type="button" onClick={() => goTo(1)} aria-label="Next photo"><ChevronRight size={26} /></button>}

        <div className="zoom-controls" aria-label="Zoom controls">
          <button className="icon-button" type="button" onClick={() => setZoom((value) => Math.max(1, value - 0.5))} disabled={zoom === 1} aria-label="Zoom out"><Minus size={17} /></button>
          <button className="zoom-value" type="button" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button className="icon-button" type="button" onClick={() => setZoom((value) => Math.min(3, value + 0.5))} disabled={zoom === 3} aria-label="Zoom in"><Plus size={17} /></button>
          <button className="icon-button" type="button" onClick={() => void enterFullscreen()} aria-label="Enter fullscreen"><Maximize2 size={17} /></button>
        </div>

        {showDetails && (
          <aside className="details-panel">
            <button className="icon-button close-details" type="button" onClick={() => setShowDetails(false)} aria-label="Close details"><X size={18} /></button>
            <p className="eyebrow">Original</p>
            <h2>Photo {currentIndex + 1} of {visiblePhotos.length}</h2>
            <dl>
              <div><dt>Dimensions</dt><dd>{photo.originalWidth && photo.originalHeight ? `${photo.originalWidth} x ${photo.originalHeight}` : "Not available"}</dd></div>
              <div><dt>File size</dt><dd>{formatBytes(photo.sizeBytes)}</dd></div>
              <div><dt>Format</dt><dd>{photo.contentType.replace("image/", "").toUpperCase()}</dd></div>
              <div><dt>Added</dt><dd>{date}</dd></div>
            </dl>
            <a className="link-button" href={photo.originalUrl} download={photo.filename}><Download size={17} />Download original</a>
          </aside>
        )}
      </section>

      <footer className="viewer-caption"><span>{gallery.displayName}</span>{status && <span role="status">{status}</span>}</footer>
    </ThemedShell>
  );
}
